import React, { useState, useEffect } from 'react';
import { connectionService } from '../services';
import { DataTable } from '../components';
import { useSideDialog, useModalDialog, useGlobalState, usePhotos, useMessage } from '../hooks';
import { handleError, Role } from '../helpers';

export const Connections = () => {
  const [connections, setConnections] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    await connectionService
      .getAll()
      .then(res => {
        const result =
          res &&
          res.connections.map(x => {
            return {
              ...x,
              connector_name_tooltip:
                x.connector_code === 'odbc'
                  ? `${x.connector_name} (${x.connector_driver_name})`
                  : x.connector_name,
            };
          });
        setConnections(result);
      })
      .catch(err => {
        showMessage(err, 'error');
      })
      .finally(res => {
        setLoading(false);
      });
  };

  const addConnection = async (data, files) => {
    const connections = { connections: Array.isArray(data) ? data : [data] };

    await connectionService
      .create(connections, files)
      .then(async res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editConnection = async (data, files) => {
    const connections = { connections: Array.isArray(data) ? data : [data] };

    await connectionService
      .update(connections, files)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteConnection = async data => {
    await connectionService
      .destroy({ connections: data })
      .then(async res => {
        fetchItems();
        hideModalDialog();
        clearSelectedItems();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;

  return (
    <div className="Connections wide-margin">
      <DataTable
        columns={[
          { id: 'connector_code', name: '', type: 'icon', tooltip: 'connector_name_tooltip' },
          {
            id: 'connection_name',
            name: 'Name',
            link: '/connections/:connection_id',
            tooltip: 'connection_description',
            className: 'bold',
          },
          {
            id: 'connector_name',
            name: 'Connector',
            className: 'small-text',
            viewMode: 'statistics',
          },
          {
            id: 'system_name',
            name: 'System',
            link: '/systems/:system_id',
            tooltip: 'system_description',
          },
          {
            id: 'connection_description',
            name: 'Description',
            type: 'description',
            viewMode: 'descriptions',
          },
          { id: 'connection_hostname', name: 'Hostname', viewMode: 'statistics' },
          { id: 'connection_port', name: 'Port', viewMode: 'statistics' },
          { id: 'connection_service', name: 'Service', viewMode: 'statistics' },
          { id: 'connection_database', name: 'Database', viewMode: 'statistics' },
        ]}
        buttons={[
          {
            label: '+ Add',
            action: 'add',
            mainButton: true,
            onClick: items =>
              showSideDialog('addConnection', items || selectedItems, addConnection),
          },
          {
            label: 'Edit',
            action: 'edit',
            onClick: items =>
              showSideDialog('editConnection', items || selectedItems, editConnection),
          },
          {
            label: 'Delete',
            action: 'delete',
            onClick: items =>
              showModalDialog('deleteConnection', items || selectedItems, deleteConnection),
          },
        ]}
        title="Connections"
        data={connections}
        idColumn="connection_id"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loading={loading}
        editable={editable}
        filterable={true}
        filterObjects={[
          'connection_name',
          'connection_description',
          'connector_name',
          'connector_category',
          'connection_hostname',
          'connection_database',
          'system_name',
        ]}
        showViewModes={true}
      />
    </div>
  );
};
